'use client';

import { useState, useCallback } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent, CreditNoteData, InvoiceLine } from '@/lib/tools/types';
import { FileText, Plus, Trash2, RotateCcw, ArrowRight, Download, Eye, X } from 'lucide-react';
import Link from 'next/link';
import { ToolSignupWall } from './tool-signup-wall';

interface CreditNoteCreatorProps {
  locale: Locale;
  content: ToolContent;
}

// Countries list: Netherlands first, then EU, Turkey, EU trade partners
const COUNTRIES = [
  // Netherlands always first
  { code: 'NL', name: 'Netherlands' },
  // EU Member States (alphabetically)
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  // Turkey
  { code: 'TR', name: 'Turkey' },
  // EU Trade Partners (EEA & Key Partners)
  { code: 'GB', name: 'United Kingdom' },
  { code: 'NO', name: 'Norway' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'IS', name: 'Iceland' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'JP', name: 'Japan' },
  { code: 'AU', name: 'Australia' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'UA', name: 'Ukraine' },
];

const VAT_RATES = [21, 9, 0];

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function CreditNoteCreator({ locale, content }: CreditNoteCreatorProps) {
  const labels = content.labels;
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [creditNote, setCreditNote] = useState<CreditNoteData>({
    invoiceNumber: `CN-${Date.now().toString().slice(-6)}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'EUR',
    originalInvoiceNumber: '',
    originalInvoiceDate: '',
    reason: 'return',
    sellerName: '',
    sellerAddress: '',
    sellerCity: '',
    sellerPostalCode: '',
    sellerCountry: 'NL',
    sellerVatNumber: '',
    sellerIban: '',
    sellerEmail: '',
    buyerName: '',
    buyerAddress: '',
    buyerCity: '',
    buyerPostalCode: '',
    buyerCountry: 'NL',
    buyerEmail: '',
    lines: [{ id: generateId(), description: '', quantity: 1, unitPrice: 0, vatRate: 21, lineTotal: 0, vatAmount: 0 }],
    subtotal: 0,
    vatAmount: 0,
    total: 0,
    notes: '',
  });

  const [showPreview, setShowPreview] = useState(false);

  const updateLine = useCallback((id: string, field: keyof InvoiceLine, value: number | string) => {
    setCreditNote(prev => {
      const newLines = prev.lines.map(line => {
        if (line.id !== id) return line;
        const updated = { ...line, [field]: value };
        const lineTotal = updated.quantity * updated.unitPrice;
        const vatAmount = lineTotal * (updated.vatRate / 100);
        return { ...updated, lineTotal, vatAmount };
      });
      const subtotal = newLines.reduce((sum, line) => sum + line.lineTotal, 0);
      const vatAmount = newLines.reduce((sum, line) => sum + line.vatAmount, 0);
      const total = subtotal + vatAmount;
      return { ...prev, lines: newLines, subtotal, vatAmount, total };
    });
  }, []);

  const addLine = () => {
    setCreditNote(prev => ({
      ...prev,
      lines: [...prev.lines, { id: generateId(), description: '', quantity: 1, unitPrice: 0, vatRate: 21, lineTotal: 0, vatAmount: 0 }],
    }));
  };

  const removeLine = (id: string) => {
    setCreditNote(prev => {
      if (prev.lines.length <= 1) return prev;
      const newLines = prev.lines.filter(line => line.id !== id);
      const subtotal = newLines.reduce((sum, line) => sum + line.lineTotal, 0);
      const vatAmount = newLines.reduce((sum, line) => sum + line.vatAmount, 0);
      const total = subtotal + vatAmount;
      return { ...prev, lines: newLines, subtotal, vatAmount, total };
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: creditNote.currency,
    }).format(value);
  };

  const getReasonLabel = (reason: string) => {
    const map: Record<string, string> = {
      return: labels.reasonReturn ?? 'Return',
      correction: labels.reasonCorrection ?? 'Correction',
      discount: labels.reasonDiscount ?? 'Discount',
      other: labels.reasonOther ?? 'Other',
    };
    return map[reason] || reason;
  };

  const generateUbl = (): string => {
    return `<?xml version="1.0" encoding="UTF-8"?>
<CreditNote xmlns="urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2"
            xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
            xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
  <cbc:CustomizationID>urn:cen.eu:en16931:2017</cbc:CustomizationID>
  <cbc:ID>${creditNote.invoiceNumber}</cbc:ID>
  <cbc:IssueDate>${creditNote.invoiceDate}</cbc:IssueDate>
  <cbc:CreditNoteTypeCode>381</cbc:CreditNoteTypeCode>
  <cbc:Note>Reason: ${getReasonLabel(creditNote.reason)}${creditNote.notes ? ` - ${creditNote.notes}` : ''}</cbc:Note>
  <cbc:DocumentCurrencyCode>${creditNote.currency}</cbc:DocumentCurrencyCode>

  <cac:BillingReference>
    <cac:InvoiceDocumentReference>
      <cbc:ID>${creditNote.originalInvoiceNumber}</cbc:ID>
      <cbc:IssueDate>${creditNote.originalInvoiceDate}</cbc:IssueDate>
    </cac:InvoiceDocumentReference>
  </cac:BillingReference>

  <cac:AccountingSupplierParty>
    <cac:Party>
      <cac:PartyName><cbc:Name>${creditNote.sellerName}</cbc:Name></cac:PartyName>
      <cac:PostalAddress>
        <cbc:StreetName>${creditNote.sellerAddress}</cbc:StreetName>
        <cbc:CityName>${creditNote.sellerCity}</cbc:CityName>
        <cbc:PostalZone>${creditNote.sellerPostalCode}</cbc:PostalZone>
        <cac:Country><cbc:IdentificationCode>${creditNote.sellerCountry}</cbc:IdentificationCode></cac:Country>
      </cac:PostalAddress>
      ${creditNote.sellerVatNumber ? `<cac:PartyTaxScheme>
        <cbc:CompanyID>${creditNote.sellerVatNumber}</cbc:CompanyID>
        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:PartyTaxScheme>` : ''}
    </cac:Party>
  </cac:AccountingSupplierParty>

  <cac:AccountingCustomerParty>
    <cac:Party>
      <cac:PartyName><cbc:Name>${creditNote.buyerName}</cbc:Name></cac:PartyName>
      <cac:PostalAddress>
        <cbc:StreetName>${creditNote.buyerAddress}</cbc:StreetName>
        <cbc:CityName>${creditNote.buyerCity}</cbc:CityName>
        <cbc:PostalZone>${creditNote.buyerPostalCode}</cbc:PostalZone>
        <cac:Country><cbc:IdentificationCode>${creditNote.buyerCountry}</cbc:IdentificationCode></cac:Country>
      </cac:PostalAddress>
    </cac:Party>
  </cac:AccountingCustomerParty>

  <cac:TaxTotal>
    <cbc:TaxAmount currencyID="${creditNote.currency}">${creditNote.vatAmount.toFixed(2)}</cbc:TaxAmount>
  </cac:TaxTotal>

  <cac:LegalMonetaryTotal>
    <cbc:LineExtensionAmount currencyID="${creditNote.currency}">${creditNote.subtotal.toFixed(2)}</cbc:LineExtensionAmount>
    <cbc:TaxExclusiveAmount currencyID="${creditNote.currency}">${creditNote.subtotal.toFixed(2)}</cbc:TaxExclusiveAmount>
    <cbc:TaxInclusiveAmount currencyID="${creditNote.currency}">${creditNote.total.toFixed(2)}</cbc:TaxInclusiveAmount>
    <cbc:PayableAmount currencyID="${creditNote.currency}">${creditNote.total.toFixed(2)}</cbc:PayableAmount>
  </cac:LegalMonetaryTotal>

${creditNote.lines.map((line, i) => `  <cac:CreditNoteLine>
    <cbc:ID>${i + 1}</cbc:ID>
    <cbc:CreditedQuantity unitCode="C62">${line.quantity}</cbc:CreditedQuantity>
    <cbc:LineExtensionAmount currencyID="${creditNote.currency}">${line.lineTotal.toFixed(2)}</cbc:LineExtensionAmount>
    <cac:Item><cbc:Name>${line.description}</cbc:Name></cac:Item>
    <cac:Price><cbc:PriceAmount currencyID="${creditNote.currency}">${line.unitPrice.toFixed(2)}</cbc:PriceAmount></cac:Price>
  </cac:CreditNoteLine>`).join('\n')}
</CreditNote>`;
  };

  // Save credit note to database
  const saveCreditNote = async (format: 'pdf' | 'ubl') => {
    try {
      await fetch('/api/tools/credit-note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credit_note_number: creditNote.invoiceNumber,
          credit_note_date: creditNote.invoiceDate,
          currency: creditNote.currency,
          locale: locale,
          original_invoice_number: creditNote.originalInvoiceNumber,
          original_invoice_date: creditNote.originalInvoiceDate,
          reason: creditNote.reason,
          seller_name: creditNote.sellerName,
          seller_address: creditNote.sellerAddress,
          seller_city: creditNote.sellerCity,
          seller_postal_code: creditNote.sellerPostalCode,
          seller_country: creditNote.sellerCountry,
          seller_vat_number: creditNote.sellerVatNumber,
          seller_iban: creditNote.sellerIban,
          seller_email: creditNote.sellerEmail,
          buyer_name: creditNote.buyerName,
          buyer_address: creditNote.buyerAddress,
          buyer_city: creditNote.buyerCity,
          buyer_postal_code: creditNote.buyerPostalCode,
          buyer_country: creditNote.buyerCountry,
          buyer_email: creditNote.buyerEmail,
          subtotal: creditNote.subtotal,
          vat_amount: creditNote.vatAmount,
          total: creditNote.total,
          lines: creditNote.lines.map(line => ({
            description: line.description,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            vatRate: line.vatRate,
            lineTotal: line.lineTotal,
            vatAmount: line.vatAmount,
          })),
          notes: creditNote.notes,
          download_format: format,
          referrer: typeof document !== 'undefined' ? document.referrer : null,
        }),
      });
    } catch (error) {
      console.error('Failed to save credit note:', error);
    }
  };

  const showSuccessToast = () => {
    setToastMessage(labels.creditNoteCreated || 'Credit note created');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const downloadUbl = async () => {
    // Save credit note to database
    await saveCreditNote('ubl');
    const xml = generateUbl();
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${creditNote.invoiceNumber}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSuccessToast();
  };

  const downloadPdf = async () => {
    // Save credit note to database
    await saveCreditNote('pdf');

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Credit Note ${creditNote.invoiceNumber}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .title { font-size: 28px; font-weight: bold; color: #dc2626; }
    .credit-number { font-size: 14px; color: #64748b; margin-top: 4px; }
    .reference { background: #fef2f2; padding: 12px; border-radius: 8px; margin-bottom: 20px; }
    .reference-label { font-size: 11px; color: #dc2626; text-transform: uppercase; }
    .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .party { width: 45%; }
    .party-label { font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 8px; }
    .party-name { font-weight: bold; font-size: 16px; margin-bottom: 4px; }
    .party-detail { font-size: 14px; color: #475569; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th { background: #fef2f2; padding: 12px; text-align: left; font-size: 12px; color: #dc2626; border-bottom: 2px solid #fecaca; }
    td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
    .text-right { text-align: right; }
    .totals { margin-top: 20px; margin-left: auto; width: 300px; }
    .totals-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
    .totals-row.grand { border-top: 2px solid #dc2626; margin-top: 8px; padding-top: 16px; font-size: 18px; font-weight: bold; color: #dc2626; }
    .watermark { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; display: flex; align-items: center; justify-content: space-between; }
    .watermark-left { display: flex; align-items: center; }
    .disclaimer { margin-top: 12px; padding: 12px 16px; background: #fef9ee; border: 1px solid #fde68a; border-radius: 6px; font-size: 11px; color: #92400e; line-height: 1.5; }
    @media print { .watermark { position: running(footer); } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">CREDIT NOTE</div>
      <div class="credit-number">${creditNote.invoiceNumber}</div>
    </div>
    <div style="text-align: right">
      <div><strong>Date:</strong> ${creditNote.invoiceDate}</div>
    </div>
  </div>

  <div class="reference">
    <div class="reference-label">Reference to Original Invoice</div>
    <div><strong>Invoice:</strong> ${creditNote.originalInvoiceNumber} | <strong>Date:</strong> ${creditNote.originalInvoiceDate}</div>
    <div><strong>Reason:</strong> ${getReasonLabel(creditNote.reason)}</div>
  </div>

  <div class="parties">
    <div class="party">
      <div class="party-label">From</div>
      <div class="party-name">${creditNote.sellerName}</div>
      <div class="party-detail">${creditNote.sellerAddress}</div>
      <div class="party-detail">${creditNote.sellerPostalCode} ${creditNote.sellerCity}</div>
      ${creditNote.sellerVatNumber ? `<div class="party-detail">VAT: ${creditNote.sellerVatNumber}</div>` : ''}
      ${creditNote.sellerEmail ? `<div class="party-detail">${creditNote.sellerEmail}</div>` : ''}
    </div>
    <div class="party">
      <div class="party-label">To</div>
      <div class="party-name">${creditNote.buyerName}</div>
      <div class="party-detail">${creditNote.buyerAddress}</div>
      <div class="party-detail">${creditNote.buyerPostalCode} ${creditNote.buyerCity}</div>
      ${creditNote.buyerEmail ? `<div class="party-detail">${creditNote.buyerEmail}</div>` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th class="text-right">Qty</th>
        <th class="text-right">Price</th>
        <th class="text-right">VAT</th>
        <th class="text-right">Credit</th>
      </tr>
    </thead>
    <tbody>
      ${creditNote.lines.map(line => `
        <tr>
          <td>${line.description || '-'}</td>
          <td class="text-right">${line.quantity}</td>
          <td class="text-right">${formatCurrency(line.unitPrice)}</td>
          <td class="text-right">${line.vatRate}%</td>
          <td class="text-right">${formatCurrency(line.lineTotal)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="totals">
    <div class="totals-row"><span>Subtotal</span><span>${formatCurrency(creditNote.subtotal)}</span></div>
    <div class="totals-row"><span>VAT</span><span>${formatCurrency(creditNote.vatAmount)}</span></div>
    <div class="totals-row grand"><span>Total Credit</span><span>${formatCurrency(creditNote.total)}</span></div>
  </div>

  ${creditNote.notes ? `<div style="margin-top: 30px; padding: 16px; background: #f8fafc; border-radius: 8px;"><strong>Notes:</strong> ${creditNote.notes}</div>` : ''}

  <div class="disclaimer">${locale === 'nl' ? 'Dit document is gegenereerd met de gratis creditnotatool van Winkel Factuur (winkelfactuur.nl). Het dient uitsluitend als voorbeeld en is niet bedoeld als juridisch of fiscaal advies.' : locale === 'de' ? 'Dieses Dokument wurde mit dem kostenlosen Gutschrift-Tool von Winkel Factuur (winkelfactuur.nl) erstellt. Es dient nur als Beispiel und stellt keine rechtliche oder steuerliche Beratung dar.' : locale === 'fr' ? 'Ce document a \u00e9t\u00e9 g\u00e9n\u00e9r\u00e9 avec l\u2019outil de cr\u00e9dit gratuit de Winkel Factuur (winkelfactuur.nl). Il est fourni \u00e0 titre d\u2019exemple uniquement et ne constitue pas un conseil juridique ou fiscal.' : 'This document was generated with the free credit note tool by Winkel Factuur (winkelfactuur.nl). It is provided as an example only and does not constitute legal or tax advice.'}</div>

  <div class="watermark">
    <div class="watermark-left">
      <img src="https://winkelfactuur.nl/bimi.svg" alt="" width="14" height="14" style="vertical-align: middle; margin-right: 6px;" />
      <span>${locale === 'nl' ? 'Gemaakt met Winkel Factuur \u2013 winkelfactuur.nl' : locale === 'de' ? 'Erstellt mit Winkel Factuur \u2013 winkelfactuur.nl' : locale === 'fr' ? 'Cr\u00e9\u00e9 avec Winkel Factuur \u2013 winkelfactuur.nl' : 'Created with Winkel Factuur \u2013 winkelfactuur.nl'}</span>
    </div>
    <span>${locale === 'nl' ? 'Gratis tool' : locale === 'de' ? 'Kostenloses Tool' : locale === 'fr' ? 'Outil gratuit' : 'Free tool'}</span>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
    showSuccessToast();
  };

  const reset = () => {
    setCreditNote({
      invoiceNumber: `CN-${Date.now().toString().slice(-6)}`,
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      currency: 'EUR',
      originalInvoiceNumber: '',
      originalInvoiceDate: '',
      reason: 'return',
      sellerName: '',
      sellerAddress: '',
      sellerCity: '',
      sellerPostalCode: '',
      sellerCountry: 'NL',
      sellerVatNumber: '',
      sellerIban: '',
      sellerEmail: '',
      buyerName: '',
      buyerAddress: '',
      buyerCity: '',
      buyerPostalCode: '',
      buyerCountry: 'NL',
      buyerEmail: '',
      lines: [{ id: generateId(), description: '', quantity: 1, unitPrice: 0, vatRate: 21, lineTotal: 0, vatAmount: 0 }],
      subtotal: 0,
      vatAmount: 0,
      total: 0,
      notes: '',
    });
    setShowPreview(false);
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100">
          <FileText className="h-7 w-7 text-red-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{content.title}</h2>
        <p className="mt-3 text-lg text-slate-600">{content.description}</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Original Invoice Reference */}
        <div className="mb-6 rounded-xl bg-red-50 p-4">
          <h2 className="mb-3 text-sm font-semibold text-red-900">{labels.originalInvoice}</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs text-red-700">{labels.originalInvoiceNumber}</label>
              <input type="text" value={creditNote.originalInvoiceNumber} onChange={e => setCreditNote(p => ({ ...p, originalInvoiceNumber: e.target.value }))} className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-red-700">{labels.originalInvoiceDate}</label>
              <input type="date" value={creditNote.originalInvoiceDate} onChange={e => setCreditNote(p => ({ ...p, originalInvoiceDate: e.target.value }))} className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-red-700">{labels.reason}</label>
              <select value={creditNote.reason} onChange={e => setCreditNote(p => ({ ...p, reason: e.target.value }))} className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20">
                <option value="return">{labels.reasonReturn}</option>
                <option value="correction">{labels.reasonCorrection}</option>
                <option value="discount">{labels.reasonDiscount}</option>
                <option value="other">{labels.reasonOther}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Credit Note Header */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">{labels.creditNoteNumber}</label>
            <input type="text" value={creditNote.invoiceNumber} onChange={e => setCreditNote(p => ({ ...p, invoiceNumber: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">{labels.creditNoteDate}</label>
            <input type="date" value={creditNote.invoiceDate} onChange={e => setCreditNote(p => ({ ...p, invoiceDate: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
        </div>

        {/* Seller */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">{labels.sellerDetails}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input type="text" placeholder={labels.companyName} value={creditNote.sellerName} onChange={e => setCreditNote(p => ({ ...p, sellerName: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input type="text" placeholder={labels.address} value={creditNote.sellerAddress} onChange={e => setCreditNote(p => ({ ...p, sellerAddress: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input type="text" placeholder={labels.postalCode} value={creditNote.sellerPostalCode} onChange={e => setCreditNote(p => ({ ...p, sellerPostalCode: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input type="text" placeholder={labels.city} value={creditNote.sellerCity} onChange={e => setCreditNote(p => ({ ...p, sellerCity: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <select value={creditNote.sellerCountry} onChange={e => setCreditNote(p => ({ ...p, sellerCountry: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <input type="text" placeholder={labels.vatNumber} value={creditNote.sellerVatNumber ?? ''} onChange={e => setCreditNote(p => ({ ...p, sellerVatNumber: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input type="email" placeholder={labels.email} value={creditNote.sellerEmail ?? ''} onChange={e => setCreditNote(p => ({ ...p, sellerEmail: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
        </div>

        {/* Buyer */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">{labels.buyerDetails}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input type="text" placeholder={labels.customerName} value={creditNote.buyerName} onChange={e => setCreditNote(p => ({ ...p, buyerName: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input type="text" placeholder={labels.address} value={creditNote.buyerAddress} onChange={e => setCreditNote(p => ({ ...p, buyerAddress: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input type="text" placeholder={labels.postalCode} value={creditNote.buyerPostalCode} onChange={e => setCreditNote(p => ({ ...p, buyerPostalCode: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input type="text" placeholder={labels.city} value={creditNote.buyerCity} onChange={e => setCreditNote(p => ({ ...p, buyerCity: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <select value={creditNote.buyerCountry} onChange={e => setCreditNote(p => ({ ...p, buyerCountry: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <input type="email" placeholder={labels.email} value={creditNote.buyerEmail ?? ''} onChange={e => setCreditNote(p => ({ ...p, buyerEmail: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
        </div>

        {/* Credit Lines */}
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">{labels.creditLines}</h2>
          <div className="space-y-3">
            {creditNote.lines.map((line) => (
              <div key={line.id} className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3">
                <div className="min-w-[200px] flex-1">
                  <label className="mb-1 block text-xs text-slate-500">{labels.description}</label>
                  <input type="text" value={line.description} onChange={e => updateLine(line.id, 'description', e.target.value)} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                </div>
                <div className="w-20">
                  <label className="mb-1 block text-xs text-slate-500">{labels.quantity}</label>
                  <input type="number" min="1" value={line.quantity} onChange={e => updateLine(line.id, 'quantity', parseFloat(e.target.value) || 0)} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                </div>
                <div className="w-28">
                  <label className="mb-1 block text-xs text-slate-500">{labels.unitPrice}</label>
                  <input type="number" min="0" step="0.01" value={line.unitPrice} onChange={e => updateLine(line.id, 'unitPrice', parseFloat(e.target.value) || 0)} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                </div>
                <div className="w-20">
                  <label className="mb-1 block text-xs text-slate-500">{labels.vatRate}</label>
                  <select value={line.vatRate} onChange={e => updateLine(line.id, 'vatRate', parseInt(e.target.value))} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm">
                    {VAT_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div className="w-28 text-right">
                  <label className="mb-1 block text-xs text-slate-500">{labels.lineTotal}</label>
                  <div className="rounded bg-white px-2 py-1.5 text-sm font-medium">{formatCurrency(line.lineTotal)}</div>
                </div>
                {creditNote.lines.length > 1 && (
                  <button type="button" onClick={() => removeLine(line.id)} className="mb-1 rounded p-1.5 text-slate-600 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine} className="mt-3 flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700">
            <Plus className="h-4 w-4" /> {labels.addLine}
          </button>
        </div>

        {/* Totals */}
        <div className="mb-6 rounded-lg bg-red-50 p-4">
          <div className="flex justify-between border-b border-red-200 pb-2 text-sm">
            <span className="text-slate-600">{labels.subtotal}</span>
            <span className="font-medium">{formatCurrency(creditNote.subtotal)}</span>
          </div>
          <div className="flex justify-between border-b border-red-200 py-2 text-sm">
            <span className="text-slate-600">{labels.vatAmount}</span>
            <span className="font-medium">{formatCurrency(creditNote.vatAmount)}</span>
          </div>
          <div className="flex justify-between pt-2 text-lg font-semibold text-red-700">
            <span>{labels.total}</span>
            <span>{formatCurrency(creditNote.total)}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium text-slate-700">{labels.notes}</label>
          <textarea rows={2} value={creditNote.notes ?? ''} onChange={e => setCreditNote(p => ({ ...p, notes: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => setShowPreview(true)} className="flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Eye className="h-4 w-4" /> {labels.preview}
          </button>
          <button type="button" onClick={downloadPdf} className="flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700">
            <Download className="h-4 w-4" /> {labels.downloadPdf}
          </button>
          <button type="button" onClick={downloadUbl} className="flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-900">
            <Download className="h-4 w-4" /> {labels.downloadUbl}
          </button>
          <button type="button" onClick={reset} className="flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <RotateCcw className="h-4 w-4" /> {labels.reset}
          </button>
        </div>

        <p className="mt-4 text-xs text-slate-500">{labels.watermarkNotice}</p>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">{labels.preview}</h3>
              <button type="button" onClick={() => setShowPreview(false)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {/* Credit Note Preview Content */}
              <div className="rounded-xl border border-red-200 bg-white p-6">
                {/* Header */}
                <div className="mb-6 flex items-start justify-between border-b border-red-200 pb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-red-600">CREDIT NOTE</h2>
                    <p className="mt-1 font-mono text-sm text-slate-600">{creditNote.invoiceNumber}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p><span className="text-slate-500">{labels.creditNoteDate}:</span> <span className="font-medium">{creditNote.invoiceDate}</span></p>
                  </div>
                </div>

                {/* Original Invoice Reference */}
                <div className="mb-6 rounded-lg bg-red-50 p-4">
                  <p className="text-xs font-semibold uppercase text-red-600">{labels.originalInvoice}</p>
                  <div className="mt-2 grid gap-2 text-sm sm:grid-cols-3">
                    <p><span className="text-slate-500">{labels.originalInvoiceNumber}:</span> <span className="font-medium">{creditNote.originalInvoiceNumber || '-'}</span></p>
                    <p><span className="text-slate-500">{labels.originalInvoiceDate}:</span> <span className="font-medium">{creditNote.originalInvoiceDate || '-'}</span></p>
                    <p><span className="text-slate-500">{labels.reason}:</span> <span className="font-medium">{getReasonLabel(creditNote.reason)}</span></p>
                  </div>
                </div>

                {/* Parties */}
                <div className="mb-6 grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase text-slate-600">{labels.sellerDetails}</p>
                    <p className="font-semibold text-slate-900">{creditNote.sellerName || '-'}</p>
                    <p className="text-sm text-slate-600">{creditNote.sellerAddress}</p>
                    <p className="text-sm text-slate-600">{creditNote.sellerPostalCode} {creditNote.sellerCity}</p>
                    {creditNote.sellerVatNumber && <p className="mt-1 text-sm text-slate-600">VAT: {creditNote.sellerVatNumber}</p>}
                    {creditNote.sellerEmail && <p className="text-sm text-slate-600">{creditNote.sellerEmail}</p>}
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase text-slate-600">{labels.buyerDetails}</p>
                    <p className="font-semibold text-slate-900">{creditNote.buyerName || '-'}</p>
                    <p className="text-sm text-slate-600">{creditNote.buyerAddress}</p>
                    <p className="text-sm text-slate-600">{creditNote.buyerPostalCode} {creditNote.buyerCity}</p>
                    {creditNote.buyerEmail && <p className="text-sm text-slate-600">{creditNote.buyerEmail}</p>}
                  </div>
                </div>

                {/* Lines Table */}
                <div className="mb-6 overflow-hidden rounded-lg border border-red-200">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-red-700">{labels.description}</th>
                        <th className="px-4 py-3 text-right font-medium text-red-700">{labels.quantity}</th>
                        <th className="px-4 py-3 text-right font-medium text-red-700">{labels.unitPrice}</th>
                        <th className="px-4 py-3 text-right font-medium text-red-700">{labels.vatRate}</th>
                        <th className="px-4 py-3 text-right font-medium text-red-700">{labels.lineTotal}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {creditNote.lines.map((line) => (
                        <tr key={line.id}>
                          <td className="px-4 py-3 text-slate-900">{line.description || '-'}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{line.quantity}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(line.unitPrice)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{line.vatRate}%</td>
                          <td className="px-4 py-3 text-right font-medium text-slate-900">{formatCurrency(line.lineTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{labels.subtotal}</span>
                      <span className="font-medium">{formatCurrency(creditNote.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{labels.vatAmount}</span>
                      <span className="font-medium">{formatCurrency(creditNote.vatAmount)}</span>
                    </div>
                    <div className="flex justify-between border-t border-red-200 pt-2 text-lg font-bold">
                      <span>{labels.total}</span>
                      <span className="text-red-600">{formatCurrency(creditNote.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {creditNote.notes && (
                  <div className="mt-6 rounded-lg bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase text-slate-600">{labels.notes}</p>
                    <p className="mt-1 text-sm text-slate-600">{creditNote.notes}</p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setShowPreview(false)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  {locale === 'nl' ? 'Sluiten' : locale === 'de' ? 'Schließen' : locale === 'fr' ? 'Fermer' : 'Close'}
                </button>
                <button type="button" onClick={() => { setShowPreview(false); downloadPdf(); }} className="flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700">
                  <Download className="h-4 w-4" /> {labels.downloadPdf}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link href={`/${locale}/signup/`} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 hover:bg-brand-50">
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {content.disclaimer.heading}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            {content.disclaimer.content}
          </p>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
